// The athlete's acceptances, asked once (fresh-start spec §3):
//   - at sign-up, one checkbox on Register covers the AI coaching consent
//     (ai_coaching: AI-generated plans, not medical advice), the Strava data
//     retention (strava_data_retention), the Terms and Privacy Policy and
//     "I'm 18 or over", recorded together by POST /consents/batch;
//   - afterwards, only when /consents/status says something is `missing` (a
//     version bump, an account from before) or `adult_confirmed` is false:
//     one "We've updated our terms" modal (consent/TermsModal.jsx, mounted in
//     Layout and onboarding).
// The server refuses every model call without a current `ai_coaching`
// consent (aithlo-backend#64): a route answers 403 { code:
// 'ai_consent_required' }, and a chat message is answered by a coach_message
// whose body.actions includes 'ai_consent'. Either way the app opens the same
// modal, never an error. Pure, so it is tested without a browser; the mobile
// app has the same rules in lib/ai-consent.ts.

export const AI_COACHING_CONSENT = { type: 'ai_coaching', version: 'v1' };
export const AI_CONSENT_CODE = 'ai_consent_required';
export const AI_CONSENT_ACTION = 'ai_consent';
export const AI_CONSENT_EVENT = 'aithlo:ai-consent-required';
// /terms.html answered 404 (29 Oct); /terms and /privacy redirect to the reader's locale.
export const TERMS_URL = 'https://aithlo.com/terms';
export const PRIVACY_URL = 'https://aithlo.com/privacy';
export const STRAVA_CONSENT_TYPE = 'strava_data_retention';
/** The versions this app's words are for. /consents/status `versions` is the source of truth; these are its fallback. */
export const KNOWN_VERSIONS = { ai_coaching: 'v1', [STRAVA_CONSENT_TYPE]: 'v1' };

/** What a refused command shows while the prompt is open: no blame, and Try again works once accepted. */
export const AI_CONSENT_FAILURE_TEXT = 'Your coach needs you to accept the AI coaching consent first. Once you have, try again.';

/** Is this answer the server asking for the AI coaching consent? */
export const isAiConsentRequired = (status, data) => status === 403 && data?.code === AI_CONSENT_CODE;

/** A coach message that offers Review consent (body.actions includes 'ai_consent'). */
export const offersAiConsent = (item) => item?.type === 'coach_message' && Array.isArray(item.body?.actions)
  && item.body.actions.includes(AI_CONSENT_ACTION);

/** Has the athlete granted `type` at its current version? */
export const hasConsent = (status, type) => status?.consents?.[type]?.granted === true;

/**
 * Every consent sign-up records, as POST /consents/batch takes them: each
 * type in `versions` from /consents/status, else the versions known here.
 */
export function signupConsents(status) {
  const versions = status?.versions && Object.keys(status.versions).length ? status.versions : KNOWN_VERSIONS;
  return Object.entries(versions).map(([consent_type, version]) => ({ consent_type, version }));
}

/** `types` as POST /consents/batch takes them, at the versions /consents/status names. */
export const consentsFor = (status, types) => types.map((t) => ({
  consent_type: t, version: status?.versions?.[t] || status?.consents?.[t]?.version || KNOWN_VERSIONS[t] || 'v1',
}));

/**
 * What the "We've updated our terms" modal asks for, or null: the consents
 * /consents/status lists as `missing` (an older backend without it: AI
 * coaching not granted, Strava retention not granted while Strava is linked)
 * and the 18+ confirmation when `adult_confirmed` is false. `requireAi`: the
 * server just refused for want of the AI consent, so it is asked even if the
 * status hasn't caught up. Never for a coach viewing an athlete: the status
 * is the viewer's own, the acceptance the athlete's.
 */
export function termsToAccept(status, { viewing = false, requireAi = false } = {}) {
  if (viewing) return null;
  const derived = [
    status?.consents?.[AI_COACHING_CONSENT.type]?.granted === false && AI_COACHING_CONSENT.type,
    status?.strava_linked && status?.consents?.[STRAVA_CONSENT_TYPE]?.granted === false && STRAVA_CONSENT_TYPE,
  ].filter(Boolean);
  const types = [...(Array.isArray(status?.missing) ? status.missing : derived)];
  if (requireAi && !types.includes(AI_COACHING_CONSENT.type)) types.unshift(AI_COACHING_CONSENT.type);
  const adult = status?.adult_confirmed === false;
  return types.length || adult ? { types, adult } : null;
}

/** Open the prompt from anywhere (the API interceptor, a thread button). */
export function requestAiConsent(win = globalThis.window) {
  try {
    win?.dispatchEvent(new CustomEvent(AI_CONSENT_EVENT));
  } catch {
    /* no window (tests, SSR): nothing to open */
  }
}
